import qs from 'qs';

import { ServiceError } from 'src/lib/errors';
import { config } from 'src/lib/config';
import { AbstractService } from 'src/services/AbstractService';
import { FORCED_RANKING_TIMEOUT } from 'src/constants';

class ForcedRankingsApi extends AbstractService implements Services.ForcedRankingsApi.ForcedRankings {
    public async getForcedRankingStrategies(
        bearerToken: string | undefined | false,
        search: App.ForcedRankings.Search,
        offset?: number,
        limit?: number,
        signal?: AbortSignal,
    ): Promise<string[]> {
        if (!bearerToken) {
            throw new ServiceError({
                message: 'Unable to perform request getForcedRankingStrategies. No bearerToken provided.',
            });
        }

        const url = `api/${this.version}/forcedRankingStrategies`;
        const searchParams = qs.stringify({
            requestor: config.appName,
            ...search,
            offset,
            limit,
        });

        try {
            return await this.api
                .get(url, {
                    searchParams,
                    signal,
                    headers: {
                        Authorization: `Bearer ${bearerToken}`,
                    },
                })
                .json<string[]>();
        } catch (e) {
            throw new ServiceError({
                ...(e as Error),
                message: 'Failed to get design concept forced ranking strategies',
                info: (e as Error).message,
                url,
                searchParams,
            });
        }
    }

    public async getForcedRankingStrategy(
        bearerToken: string | undefined | false,
        strategyName: string,
        signal?: AbortSignal,
    ): Promise<Models.IngestConfigApi.ForcedRankings.ForcedRankingStrategy> {
        if (!bearerToken) {
            throw new ServiceError({
                message: 'Unable to perform request getForcedRankingStrategy. No bearerToken provided.',
            });
        }

        const url = `api/${this.version}/forcedRankingStrategies/${strategyName}`;
        const searchParams = qs.stringify({
            requestor: config.appName,
        });

        try {
            return await this.api
                .get(url, {
                    searchParams,
                    signal,
                    headers: {
                        Authorization: `Bearer ${bearerToken}`,
                    },
                })
                .json<Models.IngestConfigApi.ForcedRankings.ForcedRankingStrategy>();
        } catch (e) {
            throw new ServiceError({
                ...(e as Error),
                message: 'Failed to get design concept forced ranking strategy',
                info: (e as Error).message,
                url,
            });
        }
    }

    public async createForcedRankingStrategy(
        bearerToken: string | undefined | false,
        strategyName: string,
        body: Partial<Models.IngestConfigApi.ForcedRankings.ForcedRankingStrategy>,
        signal?: AbortSignal,
    ): Promise<Models.IngestConfigApi.ForcedRankings.ForcedRankingStrategy> {
        if (!bearerToken) {
            throw new ServiceError({
                message: 'Unable to perform request createDesignConceptForcedRankingStrategy. No bearerToken provided.',
            });
        }

        const url = `api/${this.version}/forcedRankingStrategies/${strategyName}`;
        const searchParams = qs.stringify({
            requestor: config.appName,
        });

        try {
            return await this.api.post(url, {
                json: {
                    ...body,
                },
                searchParams,
                signal,
                headers: {
                    Authorization: `Bearer ${bearerToken}`,
                },
            })
                .json<Models.IngestConfigApi.ForcedRankings.ForcedRankingStrategy>();
        } catch (e: any) {
            if (e?.response?.status === 409) {
                throw new ServiceError({
                    ...(e as Error),
                    message: `A strategy named ${strategyName} already exists.`,
                    info: (e as Error).message,
                    url,
                });
            }
            throw new ServiceError({
                ...(e as Error),
                message: 'Failed to create design concept forced ranking strategy.',
                info: (e as Error).message,
                url,
            });
        }
    }

    public async updateForcedRankingStrategy(
        bearerToken: string | undefined | false,
        strategyName: string,
        data: Models.IngestConfigApi.ForcedRankings.ForcedRankingStrategy,
        signal?: AbortSignal,
    ): Promise<Models.IngestConfigApi.ForcedRankings.ForcedRankingStrategy> {
        if (!bearerToken) {
            throw new ServiceError({
                message: 'Unable to perform request updateForcedRankingStrategy. No bearerToken provided.',
            });
        }

        const url = `api/${this.version}/forcedRankingStrategies/${strategyName}`;
        const searchParams = qs.stringify({
            requestor: config.appName,
        });

        try {
            return await this.api
                .put(url, {
                    json: data,
                    searchParams,
                    signal,
                    timeout: FORCED_RANKING_TIMEOUT,
                    headers: {
                        Authorization: `Bearer ${bearerToken}`,
                    },
                })
                .json<Models.IngestConfigApi.ForcedRankings.ForcedRankingStrategy>();
        } catch (e) {
            throw new ServiceError({
                ...(e as Error),
                message: 'Failed to update design concept forced ranking strategy',
                info: (e as Error).message,
                url,
            });
        }
    }

    public async deleteForcedRankingStrategy(
        bearerToken: string | undefined | false,
        strategyName: string,
        signal?: AbortSignal,
    ): Promise<void> {
        if (!bearerToken) {
            throw new ServiceError({
                message: 'Unable to perform request deleteForcedRankingStrategy. No bearerToken provided.',
            });
        }

        const url = `api/${this.version}/forcedRankingStrategies/${strategyName}`;
        const searchParams = qs.stringify({
            requestor: config.appName,
        });

        try {
            await this.api.delete(url, {
                searchParams,
                signal,
                headers: {
                    Authorization: `Bearer ${bearerToken}`,
                },
            })
                .json<void>();
        } catch (e) {
            throw new ServiceError({
                ...(e as Error),
                message: 'Failed to delete design concept forced ranking strategy',
                info: (e as Error).message,
                url,
            });
        }
    }
}

export const ForcedRankingsService = new ForcedRankingsApi(config.services.ingestConfigServiceApi);
